import React, {
  useCallback, useEffect, useId, useRef, useState,
} from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';

let mermaidModulePromise;

function loadMermaid() {
  if (!mermaidModulePromise) {
    mermaidModulePromise = import('mermaid').then(({ default: mermaid }) => {
      mermaid.initialize({
        startOnLoad: false,
        theme: 'neutral',
        securityLevel: 'strict',
        flowchart: { htmlLabels: true, curve: 'basis', useMaxWidth: false },
        sequence: { useMaxWidth: false, wrap: true },
      });
      return mermaid;
    });
  }
  return mermaidModulePromise;
}

const ZOOM_STEPS = [0.75, 1, 1.25, 1.5, 1.75, 2, 2.5];

function parseSvgDimensions(svgString) {
  try {
    const doc = new DOMParser().parseFromString(svgString, 'image/svg+xml');
    const el = doc.documentElement;
    const viewBox = el.getAttribute('viewBox');
    if (viewBox) {
      const parts = viewBox.trim().split(/[\s,]+/).map(Number);
      if (parts.length === 4 && parts[2] > 0 && parts[3] > 0) {
        return { width: parts[2], height: parts[3] };
      }
    }
    const w = parseFloat(String(el.getAttribute('width') || '').replace(/px$/i, ''));
    const h = parseFloat(String(el.getAttribute('height') || '').replace(/px$/i, ''));
    if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
      return { width: w, height: h };
    }
  } catch {
    /* fall through */
  }
  return { width: 960, height: 540 };
}

function nearestZoomIndex(value) {
  let best = 0;
  let bestDiff = Infinity;
  ZOOM_STEPS.forEach((step, i) => {
    const diff = Math.abs(step - value);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = i;
    }
  });
  return best;
}

function DiagramToolbar({
  zoom,
  onZoomIn,
  onZoomOut,
  onReset,
  onFitWidth,
  onFullscreen,
  zoomInDisabled,
  zoomOutDisabled,
  compact,
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={0.5}
      sx={{
        px: 1,
        py: 0.5,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'action.hover',
        flexShrink: 0,
      }}
    >
      {!compact && (
        <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
          Drag to pan when zoomed · scroll to explore
        </Typography>
      )}
      <Stack direction="row" alignItems="center" spacing={0.25} sx={{ ml: 'auto' }}>
        <Tooltip title="Zoom out">
          <span>
            <IconButton size="small" aria-label="Zoom out diagram" disabled={zoomOutDisabled} onClick={onZoomOut}>
              <RemoveIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="caption" sx={{ minWidth: 44, textAlign: 'center', fontWeight: 600 }}>
          {Math.round(zoom * 100)}%
        </Typography>
        <Tooltip title="Zoom in">
          <span>
            <IconButton size="small" aria-label="Zoom in diagram" disabled={zoomInDisabled} onClick={onZoomIn}>
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Fit to width">
          <IconButton size="small" aria-label="Fit diagram to width" onClick={onFitWidth}>
            <FitScreenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset zoom">
          <span>
            <IconButton size="small" aria-label="Reset diagram zoom" disabled={zoom === 1} onClick={onReset}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {!compact && (
          <Tooltip title="Open fullscreen">
            <IconButton size="small" aria-label="Open diagram fullscreen" onClick={onFullscreen}>
              <OpenInFullIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
}

function DiagramViewport({
  svg,
  svgSize,
  zoom,
  maxHeight,
  fillHeight,
  onFitWidthRequest,
}) {
  const viewportRef = useRef(null);
  const panRef = useRef({ active: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 });

  const scaledWidth = svgSize.width * zoom;
  const scaledHeight = svgSize.height * zoom;

  const fitToWidth = useCallback(() => {
    const el = viewportRef.current;
    if (!el || !svgSize.width) return 1;
    const padding = 16;
    const available = Math.max(200, el.clientWidth - padding);
    return Math.min(2.5, Math.max(0.5, available / svgSize.width));
  }, [svgSize.width]);

  useEffect(() => {
    onFitWidthRequest?.(fitToWidth);
  }, [fitToWidth, onFitWidthRequest]);

  const onPointerDown = useCallback((e) => {
    if (e.pointerType === 'touch' && e.isPrimary === false) return;
    const el = viewportRef.current;
    if (!el || e.button !== 0) return;
    panRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      scrollLeft: el.scrollLeft,
      scrollTop: el.scrollTop,
    };
    el.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  }, []);

  const onPointerMove = useCallback((e) => {
    const el = viewportRef.current;
    const pan = panRef.current;
    if (!el || !pan.active) return;
    el.scrollLeft = pan.scrollLeft - (e.clientX - pan.startX);
    el.scrollTop = pan.scrollTop - (e.clientY - pan.startY);
  }, []);

  const endPan = useCallback((e) => {
    panRef.current.active = false;
    viewportRef.current?.releasePointerCapture?.(e.pointerId);
  }, []);

  return (
    <Box
      ref={viewportRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPan}
      onPointerLeave={endPan}
      sx={{
        p: { xs: 1, sm: 1.5 },
        overflow: 'auto',
        maxHeight: fillHeight ? undefined : maxHeight,
        flex: fillHeight ? 1 : undefined,
        minHeight: fillHeight ? 0 : undefined,
        width: '100%',
        touchAction: 'pan-x pan-y pinch-zoom',
        cursor: 'grab',
        bgcolor: 'grey.50',
        backgroundImage: (theme) => (
          theme.palette.mode === 'dark'
            ? 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.06) 1px, transparent 0)'
            : 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.05) 1px, transparent 0)'
        ),
        backgroundSize: '16px 16px',
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <Box
        sx={{
          width: scaledWidth,
          height: scaledHeight,
          minWidth: 'min-content',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: svgSize.width,
            height: svgSize.height,
            transform: `scale(${zoom})`,
            transformOrigin: 'top left',
            '& svg': {
              display: 'block',
              width: svgSize.width,
              height: svgSize.height,
              maxWidth: 'none',
            },
          }}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </Box>
    </Box>
  );
}

function DiagramFrame({
  svg,
  svgSize,
  zoom,
  setZoom,
  maxHeight,
  compactToolbar,
  onFullscreen,
  fillHeight,
}) {
  const fitWidthFnRef = useRef(() => 1);

  const onZoomIn = () => setZoom((z) => {
    const idx = nearestZoomIndex(z);
    return ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, idx + 1)];
  });
  const onZoomOut = () => setZoom((z) => {
    const idx = nearestZoomIndex(z);
    return ZOOM_STEPS[Math.max(0, idx - 1)];
  });
  const onReset = () => setZoom(1);
  const onFitWidth = () => setZoom(fitWidthFnRef.current());

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', minHeight: 0, flex: fillHeight ? 1 : undefined,
    }}
    >
      <DiagramToolbar
        zoom={zoom}
        onZoomIn={onZoomIn}
        onZoomOut={onZoomOut}
        onReset={onReset}
        onFitWidth={onFitWidth}
        onFullscreen={onFullscreen}
        zoomInDisabled={zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1]}
        zoomOutDisabled={zoom <= ZOOM_STEPS[0]}
        compact={compactToolbar}
      />
      <DiagramViewport
        svg={svg}
        svgSize={svgSize}
        zoom={zoom}
        maxHeight={maxHeight}
        fillHeight={fillHeight}
        onFitWidthRequest={(fn) => { fitWidthFnRef.current = fn; }}
      />
    </Box>
  );
}

export default function StudioHelpMermaid({ chart }) {
  const reactId = useId().replace(/:/g, '');
  const [svg, setSvg] = useState('');
  const [svgSize, setSvgSize] = useState({ width: 960, height: 540 });
  const [error, setError] = useState('');
  const [zoom, setZoom] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const autoFitDoneRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const source = String(chart || '').trim();
    if (!source) return undefined;

    setSvg('');
    setError('');
    setZoom(1);
    autoFitDoneRef.current = false;

    loadMermaid()
      .then(async (mermaid) => {
        const id = `studio-help-mermaid-${reactId}`;
        const { svg: rendered } = await mermaid.render(id, source);
        if (!cancelled) {
          setSvg(rendered);
          setSvgSize(parseSvgDimensions(rendered));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'Could not render diagram.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [chart, reactId]);

  // Auto fit-to-width once when wide diagrams load in the inline viewer
  useEffect(() => {
    if (!svg || autoFitDoneRef.current) return undefined;
    const t = window.setTimeout(() => {
      autoFitDoneRef.current = true;
      if (svgSize.width > 720) {
        setZoom(Math.min(1, 680 / svgSize.width));
      }
    }, 100);
    return () => window.clearTimeout(t);
  }, [svg, svgSize.width]);

  if (error) {
    return (
      <Box
        sx={{
          my: 2,
          p: 2,
          borderRadius: 1,
          border: 1,
          borderColor: 'error.light',
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
          Diagram render error
        </Typography>
        <Box component="pre" sx={{ m: 0, fontSize: 12, overflow: 'auto' }}>{chart}</Box>
      </Box>
    );
  }

  if (!svg) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3, my: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          my: 2,
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DiagramFrame
          svg={svg}
          svgSize={svgSize}
          zoom={zoom}
          setZoom={setZoom}
          maxHeight="min(72vh, 720px)"
          onFullscreen={() => setFullscreen(true)}
        />
      </Box>

      <Dialog
        open={fullscreen}
        onClose={() => setFullscreen(false)}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 'min(96vw, 1400px)',
            height: 'min(92vh, 900px)',
            maxWidth: 'none',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ py: 1, px: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" component="span" sx={{ flex: 1 }}>
            Diagram
          </Typography>
          <IconButton size="small" aria-label="Close fullscreen" onClick={() => setFullscreen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <DiagramFrame
            svg={svg}
            svgSize={svgSize}
            zoom={zoom}
            setZoom={setZoom}
            fillHeight
            compactToolbar
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
